import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useLocation, useNavigationType, useNavigate } from 'react-router-dom';

const HistoryContext = createContext();

const SESSION_STORAGE_KEY = 'app_history';

export function AppHistoryProvider({ children }) {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const history = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY)) || [];

    if (navigationType === 'PUSH') {
      history.push(location.key);
    } else if (navigationType === 'POP') {
      // When the user navigates back, the key is removed from the stack
      // This is a simplification; browser forward/back can be complex.
      // This implementation assumes POP is always a "back" action for our purpose.
      history.pop();
    }

    // On first load or refresh, the stack is reset to the current page key
    if (history.length === 0) {
      history.push(location.key);
    }

    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(history));
  }, [location, navigationType]);

  return (
    <HistoryContext.Provider value={null}>{children}</HistoryContext.Provider>
  );
}

export function useAppHistory() {
  const navigate = useNavigate();

  const goBack = useCallback((fallbackPath = '/console') => {
    const history = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY)) || [];

    // If our history stack has more than one entry, we can safely go back.
    if (history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallbackPath, { replace: true });
    }
  }, [navigate]);

  return { goBack };
}
